import { combineLatest, filter, map, Observable, of, Subject, switchMap } from 'rxjs';

import { inject, Injectable } from '@angular/core';
import { collectionData, doc, docData, DocumentReference, getDocs, query, QuerySnapshot, Timestamp, where } from '@angular/fire/firestore';
import { AuthService } from '@auth/services/auth/auth.service';
import { ORGANISATION_COST_PER_USER_PER_MONTH } from '@organisations/classes/consts';
import { IOrganisationBill } from '@organisations/models/organisation-bill';
import {
	IOrganisationAndOrganisationUser,
	IOrganisationUser,
	IUserAndOrganisationUser,
	OrganisationMemberStatus,
	OrganisationUserAccessLevel,
} from '@organisations/models/organisation-user';
import { IOrganisation } from '@organisations/models/organisations';
import { FireStoreCollection } from '@shared/models/firestore';
import { IMobileNumber } from '@shared/models/numbers';
import { FirestoreService } from '@shared/services/firestore/firestore.service';
import { IUser } from '@user/models/user';
import { UsersService } from '@user/services/users/users.service';

@Injectable({
	providedIn: 'root',
})
export class OrganisationUserService extends FirestoreService<IOrganisationUser> {
	private authService = inject(AuthService);
	private usersService = inject(UsersService);

	userHasOrganisationInvitations$: Subject<boolean> = new Subject<boolean>();

	constructor() {
		super(FireStoreCollection.ORGANISATION_USER);
	}

	organisationsByCurrentUser(): Observable<Array<IOrganisationUser>> {
		return this.authService.currentUser$.pipe(
			switchMap((user) => {
				const userRef = doc(this.firestore, `${FireStoreCollection.USERS}/${user?.uid}`);
				const q = query(this.collectionRef, where('user', '==', userRef), where('status', '==', OrganisationMemberStatus.ACTIVE));
				return collectionData<IOrganisationUser>(q, { idField: 'id' });
			}),
		);
	}

	availableOrganisations(): Observable<Array<IOrganisationAndOrganisationUser>> {
		return this.organisationsByCurrentUser().pipe(
			switchMap((organisationUsers: Array<IOrganisationUser>) => {
				if (organisationUsers.length === 0) {
					return of([]);
				}
				const organisationUserWithOrgData$ = organisationUsers.map((organisationUser) => {
					const organisationDocRef = organisationUser.organisation as DocumentReference<IOrganisation, IOrganisation>;
					const organisationData$ = docData<IOrganisation>(organisationDocRef, {
						idField: 'id',
					});
					return organisationData$.pipe(
						filter((organisationData) => !!organisationData),
						map((organisationData) => ({
							organisation: organisationData,
							organisationUser: organisationUser,
						})),
					);
				});
				return combineLatest(organisationUserWithOrgData$);
			}),
		);
	}

	visibleUsersByOrganisation(organisationRef: DocumentReference): Observable<Array<IUserAndOrganisationUser>> {
		const q = query(
			this.collectionRef,
			where('organisation', '==', organisationRef),
			where('status', 'in', [
				OrganisationMemberStatus.ACTIVE,
				OrganisationMemberStatus.INVITATION_PENDING,
				OrganisationMemberStatus.DECLINED,
			]),
			where('accessLevel', '!=', OrganisationUserAccessLevel.ADMIN),
		);
		return collectionData<IOrganisationUser>(q, { idField: 'id' }).pipe(
			switchMap((organisationUsers: Array<IOrganisationUser>) => {
				if (organisationUsers.length === 0) {
					return of([]);
				}
				const organisationUserWithUserData$ = organisationUsers.map((organisationUser) => {
					const organisationUserRef = organisationUser.user as DocumentReference<IUser, IUser>;
					if (!organisationUserRef) {
						return of({
							organisationUser: organisationUser,
							user: {} as IUser,
						});
					}
					const userData$ = docData<IUser>(organisationUserRef, { idField: 'id' });
					return userData$.pipe(
						map((userData) => ({
							organisationUser: organisationUser,
							user: userData as IUser,
						})),
					);
				});
				return combineLatest(organisationUserWithUserData$);
			}),
		);
	}

	activeUsersByOrganisationRef(organisationRef: DocumentReference): Observable<Array<IUserAndOrganisationUser>> {
		const q = query(
			this.collectionRef,
			where('organisation', '==', organisationRef),
			where('status', '==', OrganisationMemberStatus.ACTIVE),
			where('accessLevel', '!=', OrganisationUserAccessLevel.ADMIN),
		);
		return collectionData<IOrganisationUser>(q, { idField: 'id' }).pipe(
			switchMap((organisationUsers: Array<IOrganisationUser>) => {
				if (organisationUsers.length === 0) {
					return of([]);
				}
				const organisationUserWithUserData$ = organisationUsers.map((organisationUser) => {
					const organisationUserRef = organisationUser.user as DocumentReference<IUser, IUser>;
					const userData$ = docData<IUser>(organisationUserRef, { idField: 'id' });
					return userData$.pipe(
						map((userData) => ({
							organisationUser: organisationUser,
							user: userData as IUser,
						})),
					);
				});
				return combineLatest(organisationUserWithUserData$);
			}),
		);
	}

	async inviteUserToOrganisationByEmail(
		email: string,
		accessLevel: OrganisationUserAccessLevel,
		organisationRef: DocumentReference,
	): Promise<DocumentReference<IOrganisationUser> | null> {
		email = email.toLocaleLowerCase();
		try {
			const user = await this.usersService.userByEmail(email);
			if (!user) {
				return null;
			}
			const userRef = doc(this.firestore, `${FireStoreCollection.USERS}/${user.uid}`);
			return this.inviteUserToOrganisationByUser(userRef, accessLevel, organisationRef);
		} catch (error) {
			return null;
		}
	}

	async inviteUserToOrganisationByPhone(
		phoneNumber: IMobileNumber,
		accessLevel: OrganisationUserAccessLevel,
		organisationRef: DocumentReference,
	): Promise<DocumentReference<IOrganisationUser> | null> {
		try {
			const user = await this.usersService.userByPhoneNumber(phoneNumber);
			if (!user) {
				return null;
			}
			const userRef = doc(this.firestore, `${FireStoreCollection.USERS}/${user.uid}`);
			return this.inviteUserToOrganisationByUser(userRef, accessLevel, organisationRef);
		} catch (error) {
			return null;
		}
	}

	async inviteUserToOrganisationByUser(
		userRef: DocumentReference,
		accessLevel: OrganisationUserAccessLevel,
		organisationRef: DocumentReference,
	): Promise<DocumentReference<IOrganisationUser> | null> {
		try {
			if (!userRef || !organisationRef) {
				return null;
			}

			const existingOrgUser = await this.organisationUserExistsForUser(userRef, organisationRef);

			if (existingOrgUser) {
				return null;
			}

			const result = await this.create({
				user: userRef,
				accessLevel: accessLevel,
				createdDate: Timestamp.now(),
				updatedDate: Timestamp.now(),
				joinDate: null,
				removeDate: null,
				organisation: organisationRef,
				status: OrganisationMemberStatus.INVITATION_PENDING,
			});
			return result;
		} catch (error) {
			return null;
		}
	}

	async removeUserFromOrganisation(organisationUserId: string) {
		try {
			const result = await super.update(organisationUserId, {
				status: OrganisationMemberStatus.REMOVED,
				updatedDate: Timestamp.now(),
				removeDate: Timestamp.now(),
			});
			return result;
		} catch (error) {
			return null;
		}
	}

	async organisationUserExistsForUser(userRef: DocumentReference, organisationRef: DocumentReference): Promise<boolean> {
		const q = query(
			this.collectionRef,
			where('organisation', '==', organisationRef),
			where('user', '==', userRef),
			where('status', 'in', [OrganisationMemberStatus.ACTIVE, OrganisationMemberStatus.INVITATION_PENDING]),
		);
		const snapshot = await getDocs(q);

		return !snapshot.empty;
	}

	invitationsByCurrentUser(): Observable<Array<IOrganisationUser>> {
		const userRef = doc(this.firestore, `${FireStoreCollection.USERS}/${this.authService.currentUser?.uid}`);
		const q = query(
			this.collectionRef,
			where('user', '==', userRef),
			where('status', '==', OrganisationMemberStatus.INVITATION_PENDING),
		);
		return collectionData<IOrganisationUser>(q, { idField: 'id' });
	}

	organisationInvitations(): Observable<Array<IOrganisationAndOrganisationUser>> {
		return this.invitationsByCurrentUser().pipe(
			switchMap((organisationUsers: Array<IOrganisationUser>) => {
				if (organisationUsers.length === 0) {
					return of([]);
				}
				const organisationUserWithOrgData$ = organisationUsers.map((organisationUser) => {
					const organisationDocRef = organisationUser.organisation as DocumentReference<IOrganisation, IOrganisation>;
					const organisationData$ = docData<IOrganisation>(organisationDocRef, {
						idField: 'id',
					});
					return organisationData$.pipe(
						filter((organisationData) => !!organisationData),
						map((organisationData) => ({
							organisation: organisationData,
							organisationUser: organisationUser,
						})),
					);
				});
				return combineLatest(organisationUserWithOrgData$);
			}),
		);
	}

	acceptInvitation(organisationAndOrganisationUser: IOrganisationAndOrganisationUser) {
		const user = this.authService.currentUser;
		if (organisationAndOrganisationUser.organisationUser.id) {
			return this.update(organisationAndOrganisationUser.organisationUser.id, {
				status: OrganisationMemberStatus.ACTIVE,
				updatedDate: Timestamp.now(),
				joinDate: Timestamp.now(),
				user: doc(this.firestore, `${FireStoreCollection.USERS}/${user?.uid}`),
			});
		}
		return null;
	}

	rejectInvitation(organisationAndOrganisationUser: IOrganisationAndOrganisationUser) {
		if (organisationAndOrganisationUser.organisationUser.id) {
			return this.update(organisationAndOrganisationUser.organisationUser.id, {
				status: OrganisationMemberStatus.DECLINED,
				updatedDate: Timestamp.now(),
			});
		}
		return null;
	}

	async ownersForOrganisation(organisationId: string): Promise<QuerySnapshot<IOrganisationUser>> {
		const organisationRef = doc(this.firestore, `${FireStoreCollection.ORGANISATIONS}/${organisationId}`);
		const q = query(
			this.collectionRef,
			where('organisation', '==', organisationRef),
			where('accessLevel', '==', OrganisationUserAccessLevel.OWNER),
			where('status', '==', OrganisationMemberStatus.ACTIVE),
		);
		const result = await getDocs(q);
		return result;
	}

	async updateUserAccessLevel(organisationId: string, organisationUserId: string, accessLevel: OrganisationUserAccessLevel) {
		try {
			const owners = await this.ownersForOrganisation(organisationId);

			if (owners.size <= 1 && accessLevel !== OrganisationUserAccessLevel.OWNER) {
				return false;
			}

			await this.update(organisationUserId, {
				accessLevel: accessLevel,
				updatedDate: Timestamp.now(),
			});

			return true;
		} catch (error) {
			return false;
		}
	}

	billableUsersByOrganisation(organisationRef: DocumentReference, month: number, year: number): Observable<IOrganisationBill> {
		const startOfMonth = new Date(year, month - 1, 1);
		const endOfMonth = new Date(year, month, 0);

		const activeUsersQuery = query(
			this.collectionRef,
			where('organisation', '==', organisationRef),
			where('status', 'in', [OrganisationMemberStatus.ACTIVE, OrganisationMemberStatus.CURRENTLY_LOGGED_IN]),
			where('accessLevel', '!=', OrganisationUserAccessLevel.ADMIN),
			where('joinDate', '<=', Timestamp.fromDate(endOfMonth)),
		);

		const removedUsersQuery = query(
			this.collectionRef,
			where('organisation', '==', organisationRef),
			where('status', '==', OrganisationMemberStatus.REMOVED),
			where('joinDate', '<=', Timestamp.fromDate(endOfMonth)),
			where('removeDate', '>=', Timestamp.fromDate(startOfMonth)),
			where('removeDate', '<=', Timestamp.fromDate(endOfMonth)),
		);

		const activeUsers$ = collectionData<IOrganisationUser>(activeUsersQuery, { idField: 'id' });
		const removedUsers$ = collectionData<IOrganisationUser>(removedUsersQuery, { idField: 'id' });

		return combineLatest([activeUsers$, removedUsers$]).pipe(
			switchMap(([activeUsers, removedUsers]) => {
				const users = [...activeUsers, ...removedUsers];

				if (users.length === 0) {
					return of({ invoices: [], totalAmount: 0 });
				}

				const invoiceObservables = users.map((orgUser) => {
					const joinDate = orgUser.joinDate?.toDate() ?? startOfMonth;
					const effectiveJoinDate = joinDate < startOfMonth ? startOfMonth : joinDate;

					const removeDate =
						orgUser.status === OrganisationMemberStatus.REMOVED && orgUser.removeDate
							? orgUser.removeDate.toDate()
							: endOfMonth;
					const effectiveRemoveDate = removeDate > endOfMonth ? endOfMonth : removeDate;

					const daysActive = Math.max(
						(effectiveRemoveDate.getTime() - effectiveJoinDate.getTime()) / (1000 * 60 * 60 * 24) + 1,
						0,
					);
					const daysInMonth = endOfMonth.getDate();
					const billingAmount = (daysActive / daysInMonth) * ORGANISATION_COST_PER_USER_PER_MONTH;

					const userRef = orgUser.user as DocumentReference<IUser>;
					return docData<IUser>(userRef, { idField: 'id' }).pipe(
						map((userData) => ({
							user: userData as IUser,
							billingAmount,
							joinDate: orgUser.joinDate?.toDate(),
							removeDate: orgUser.removeDate?.toDate(),
							daysActive,
							status: orgUser.status,
						})),
					);
				});

				return combineLatest(invoiceObservables).pipe(
					map((invoices) => {
						const totalAmount = invoices.reduce((sum, i) => sum + i.billingAmount, 0);
						return { invoices, totalAmount };
					}),
				);
			}),
		);
	}
}
